import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { CheckCircle2, ClipboardCopy, Eye, Link2, Loader2, Pencil, Plus, RefreshCw, Rss, Stethoscope, XCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import socialService from '@services/socialService';
import type { ConnectionReport, Network, OAuthNetwork, SaveAccountRequest, SocialAccount } from '@/types/social';
import { ACCOUNT_STATUS_LABELS, NETWORKS, NETWORK_LABELS, OAUTH_NETWORKS } from '@/types/social';
import { accountLabel, formatCount, formatPostTime } from '@/utils/social';
import { NetworkIcon } from '@components/social/SocialBits';
import { AccountAvatar } from '@components/social/ProfileSwitcher';
import { ConnectionReportDialog } from '@components/social/ConnectionReportDialog';

const oauthKeyOf = (n: Network): OAuthNetwork => (n === 'instagram' || n === 'facebook' ? 'meta' : (n as OAuthNetwork));

/** Linha curta com os números do perfil (quando já lidos da rede). */
const profileLine = (a: SocialAccount): string =>
  [
    a.followers_count !== undefined && `${formatCount(a.followers_count)} seguidores`,
    a.media_count !== undefined && `${formatCount(a.media_count)} ${a.network === 'pinterest' ? 'pins' : 'publicações'}`,
  ]
    .filter(Boolean)
    .join(' · ');

const empty = (): SaveAccountRequest => ({ network: 'instagram', username: '', publish_mode: 'reminder', is_active: true });

const toReq = (a: SocialAccount): SaveAccountRequest => ({
  network: a.network,
  username: a.username,
  display_name: a.display_name ?? '',
  external_id: a.external_id ?? '',
  avatar_url: a.avatar_url ?? '',
  profile_url: a.profile_url ?? '',
  access_token: a.access_token ?? '',
  refresh_token: a.refresh_token ?? '',
  token_expires_at: a.token_expires_at ? a.token_expires_at.slice(0, 16) : '',
  scopes: a.scopes ?? '',
  params_json: a.params_json ?? '',
  publish_mode: a.publish_mode,
  is_active: a.is_active,
});

/**
 * Contas das redes (só admin). "Conectar" faz o login na rede (OAuth) e grava a conta com os
 * tokens; conta conectada pode publicar sozinha (modo automático). Cadastro manual = lembrete.
 * Um login da Meta traz todos os perfis (cada Página + o Instagram dela); ao voltar, o diagnóstico
 * mostra o que veio e o que ficou de fora. Tokens aparecem em claro (regra das integrações).
 * Documentação: src/docs/redes-sociais/contas.md.
 */
export const SocialAccountsView: React.FC = () => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ id: number | null; data: SaveAccountRequest } | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [report, setReport] = useState<ConnectionReport | null>(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // Volta do login da rede: ?connected=meta&count=2&report=ID ou ?oauth_error=...&report=ID
  useEffect(() => {
    const connected = params.get('connected');
    const oauthError = params.get('oauth_error');
    const reportId = Number(params.get('report'));
    if (reportId > 0) {
      socialService
        .getConnectionReport(reportId)
        .then((r) => {
          // Sem avisos, o aviso verde basta; com avisos, o diagnóstico abre sozinho.
          if (r.warnings > 0 || r.errors > 0) setReport(r);
        })
        .catch(() => undefined);
    }
    if (!connected && !oauthError) return;
    if (connected) {
      const label = OAUTH_NETWORKS.find((n) => n.key === connected)?.label ?? connected;
      setNotice({ ok: true, text: `${label}: ${params.get('count') ?? ''} conta(s) conectada(s).` });
    } else {
      setNotice({ ok: false, text: `Não foi possível conectar: ${oauthError}` });
    }
    setParams({}, { replace: true });
  }, [params, setParams]);

  const connect = async (network: OAuthNetwork) => {
    setBusy(`connect-${network}`);
    try {
      window.location.href = await socialService.oauthStart(network);
    } catch (err) {
      setError(err);
      setBusy(null);
    }
  };

  const lastReport = async () => {
    setBusy('report');
    try {
      const [r] = await socialService.listConnectionReports('meta');
      if (r) setReport(r);
      else setNotice({ ok: false, text: 'Ainda não há diagnóstico: clique em Conectar Instagram e Facebook.' });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const accountAction = async (a: SocialAccount, action: 'refresh' | 'feed') => {
    setBusy(`${action}-${a.id}`);
    try {
      if (action === 'refresh') {
        await socialService.refreshAccount(a.id);
        setNotice({ ok: true, text: `Token de @${a.username} renovado.` });
      } else {
        const n = await socialService.syncFeed(a.id);
        setNotice({ ok: true, text: `Perfil de ${accountLabel(a)} atualizado${n > 0 ? ` e ${n} post(s) do feed lidos para a grade` : ''}.` });
      }
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAccounts(await socialService.manageAccounts());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await socialService.saveAccount(form.id, form.data);
      setForm(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const set = (patch: Partial<SaveAccountRequest>) => setForm((f) => (f ? { ...f, data: { ...f.data, ...patch } } : f));

  const field = (label: string, key: keyof SaveAccountRequest, opts: { placeholder?: string; secret?: boolean; multiline?: boolean } = {}) => {
    const value = (form?.data[key] as string | null | undefined) ?? '';
    return (
      <div>
        <div className="flex items-center justify-between">
          <label className="text-small font-medium text-slate-700">{label}</label>
          {opts.secret && value && (
            <button type="button" className="text-[11px] text-emerald-700 inline-flex items-center gap-1" onClick={() => navigator.clipboard.writeText(value).catch(() => undefined)}>
              <ClipboardCopy size={11} /> copiar
            </button>
          )}
        </div>
        {opts.multiline ? (
          <textarea className="input-field w-full font-mono text-xs min-h-[60px]" placeholder={opts.placeholder} value={value} onChange={(e) => set({ [key]: e.target.value } as Partial<SaveAccountRequest>)} />
        ) : (
          <input
            className={clsx('input-field w-full', opts.secret && 'font-mono text-xs')}
            placeholder={opts.placeholder}
            value={value}
            onChange={(e) => set({ [key]: e.target.value } as Partial<SaveAccountRequest>)}
          />
        )}
      </div>
    );
  };

  const rowActions = (a: SocialAccount) => (
    <>
      {(a.status === 'expired' || a.status === 'error') && (
        <Button size="sm" variant="outline" icon={<Link2 />} onClick={() => connect(oauthKeyOf(a.network))}>
          Reconectar
        </Button>
      )}
      {a.status === 'connected' && a.refresh_token && (
        <Button size="sm" variant="outline" icon={<RefreshCw />} loading={busy === `refresh-${a.id}`} onClick={() => accountAction(a, 'refresh')}>
          Renovar token
        </Button>
      )}
      {a.status === 'connected' && (a.network === 'instagram' || a.network === 'pinterest' || a.network === 'facebook') && (
        <Button size="sm" variant="outline" icon={<Rss />} loading={busy === `feed-${a.id}`} onClick={() => accountAction(a, 'feed')}>
          {a.network === 'facebook' ? 'Ler perfil' : 'Ler feed'}
        </Button>
      )}
      {a.is_active && (
        <Button size="sm" variant="outline" icon={<Eye />} onClick={() => navigate(`/social/grid?network=${a.network}&account=${a.id}`)}>
          Ver perfil
        </Button>
      )}
      <Button size="sm" variant="outline" icon={<Pencil />} onClick={() => setForm({ id: a.id, data: toReq(a) })}>
        Editar
      </Button>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-small text-slate-500 max-w-3xl">
          Contas usadas nos posts. Conta <strong>conectada</strong> em modo <strong>automático</strong> publica sozinha na hora marcada; as demais
          funcionam em <strong>modo lembrete</strong> (aviso no sino e publicação pelo app, com mídia e legenda prontas no post).
        </p>
        {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
        <Button size="sm" icon={<Plus />} onClick={() => setForm({ id: null, data: empty() })}>
          Nova conta
        </Button>
      </div>
      {notice && (
        <div
          className={clsx(
            'rounded-lg border px-3 py-2 text-sm flex items-center gap-2',
            notice.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700',
          )}
        >
          {notice.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
          <span className="flex-1 [overflow-wrap:anywhere]">{notice.text}</span>
          <button type="button" className="text-xs underline" onClick={() => setNotice(null)}>
            fechar
          </button>
        </div>
      )}

      <section className="card p-3 space-y-2">
        <h2 className="text-small font-semibold text-slate-700">Conectar pela rede (publicação automática)</h2>
        <p className="text-[11px] text-slate-500">
          Abre o login da rede com a conta que administra o perfil/página. Antes, o app da rede precisa estar configurado em Integrações — veja o guia{' '}
          <Link to="/help?doc=./guias/configurar-redes.md" className="text-emerald-700 hover:underline">
            Como configurar o acesso de cada rede social
          </Link>
          .
        </p>
        <p className="text-[11px] text-slate-500">
          <strong>Vários perfis do Instagram</strong> (Viver Organizer, Projetos, Cris Faria…) entram num login só: no diálogo da Meta, em
          &quot;Editar acesso&quot;, marque <strong>todas</strong> as Páginas e perfis. Cada Instagram precisa ser conta profissional e estar vinculado a
          uma Página. O <strong>diagnóstico</strong> mostra o que veio e o que ficou de fora.
        </p>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          {OAUTH_NETWORKS.map((n) => (
            <Button key={n.key} size="sm" variant="outline" loading={busy === `connect-${n.key}`} onClick={() => connect(n.key)}>
              <span className="inline-flex items-center gap-1">
                {n.networks.map((x) => (
                  <NetworkIcon key={x} network={x} size={13} />
                ))}
                {n.label}
              </span>
            </Button>
          ))}
          <Button size="sm" variant="outline" icon={<Stethoscope />} loading={busy === 'report'} onClick={lastReport}>
            Último diagnóstico
          </Button>
        </div>
      </section>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {accounts.map((a) => (
          <div key={a.id} className={clsx('card p-3 space-y-1', !a.is_active && 'opacity-50')}>
            <div className="flex items-center gap-2">
              <AccountAvatar account={a} size="md" />
              <span className="flex-1 min-w-0">
                <span className="block font-medium truncate">{accountLabel(a)}</span>
                {profileLine(a) && <span className="block text-[11px] text-slate-500">{profileLine(a)}</span>}
              </span>
              <span className={clsx('text-xs', a.status === 'connected' ? 'text-emerald-700' : a.status === 'manual' ? 'text-slate-500' : 'text-red-600')}>
                {ACCOUNT_STATUS_LABELS[a.status]}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {a.publish_mode === 'auto' ? 'Automático' : 'Lembrete'}
              {a.token_expires_at && ` · token vence ${formatPostTime(a.token_expires_at)}`}
            </p>
            {a.status_message && <p className="text-xs text-red-600">{a.status_message}</p>}
            <div className="flex flex-wrap gap-1 pt-1">{rowActions(a)}</div>
          </div>
        ))}
      </div>

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="table-header">
            <tr>
              <th>Rede</th>
              <th>Conta</th>
              <th>Modo</th>
              <th>Situação</th>
              <th>Token vence</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className={clsx('table-row', !a.is_active && 'opacity-50')}>
                <td className="table-cell">
                  <span className="inline-flex items-center gap-1.5">
                    <NetworkIcon network={a.network} /> {NETWORK_LABELS[a.network]}
                  </span>
                </td>
                <td className="table-cell">
                  <div className="flex items-center gap-2">
                    <AccountAvatar account={a} size="md" />
                    <div className="min-w-0">
                      <p className="font-medium">{accountLabel(a)}</p>
                      {a.display_name && a.network !== 'facebook' && <p className="text-xs text-slate-500">{a.display_name}</p>}
                      {profileLine(a) && <p className="text-[11px] text-slate-500">{profileLine(a)}</p>}
                    </div>
                  </div>
                </td>
                <td className="table-cell">{a.publish_mode === 'auto' ? 'Automático' : 'Lembrete'}</td>
                <td className="table-cell">
                  <span className={clsx(a.status === 'connected' ? 'text-emerald-700' : a.status === 'manual' ? 'text-slate-600' : 'text-red-600')}>
                    {ACCOUNT_STATUS_LABELS[a.status]}
                  </span>
                  {!a.is_active && ' · inativa'}
                  {a.status_message && <p className="text-[11px] text-red-600 max-w-xs">{a.status_message}</p>}
                </td>
                <td className="table-cell">{formatPostTime(a.token_expires_at) || '—'}</td>
                <td className="table-cell">
                  <div className="flex flex-wrap justify-end gap-1">{rowActions(a)}</div>
                </td>
              </tr>
            ))}
            {accounts.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="table-cell text-center text-slate-500 py-8">
                  Nenhuma conta cadastrada. Cadastre ao menos o Instagram para escolher a conta nos posts.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Editar conta' : 'Nova conta'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={save}>
              Salvar
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-small font-medium text-slate-700">Rede</label>
                <select className="select-field w-full" value={form.data.network} onChange={(e) => set({ network: e.target.value as Network })}>
                  {NETWORKS.map((n) => (
                    <option key={n} value={n}>
                      {NETWORK_LABELS[n]}
                    </option>
                  ))}
                </select>
              </div>
              {field('@perfil, página ou canal', 'username', { placeholder: 'viverorganizer' })}
              {field('Nome de exibição', 'display_name')}
              {field('Link do perfil', 'profile_url', { placeholder: 'https://instagram.com/viverorganizer' })}
              <div>
                <label className="text-small font-medium text-slate-700">Modo de publicação</label>
                <select className="select-field w-full" value={form.data.publish_mode} onChange={(e) => set({ publish_mode: e.target.value as 'reminder' | 'auto' })}>
                  <option value="reminder">Lembrete (publica pelo app)</option>
                  <option value="auto" disabled={!accounts.find((a) => a.id === form.id && a.status === 'connected')}>
                    Automático (exige a conta conectada)
                  </option>
                </select>
              </div>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 mt-6">
                <input type="checkbox" className="checkbox-field" checked={form.data.is_active} onChange={(e) => set({ is_active: e.target.checked })} /> Ativa
              </label>
            </div>
            <details className="rounded-lg border border-slate-200 p-3">
              <summary className="text-sm font-medium text-slate-700 cursor-pointer">Dados técnicos (preenchidos pela conexão com a rede)</summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {field('ID na rede', 'external_id', { placeholder: 'ig-user-id / page-id / channel-id' })}
                {field('Token vence em (AAAA-MM-DDTHH:MM)', 'token_expires_at')}
                {field('Access token', 'access_token', { secret: true, multiline: true })}
                {field('Refresh token', 'refresh_token', { secret: true, multiline: true })}
                {field('Escopos', 'scopes')}
                {field('Parâmetros extras (JSON)', 'params_json', { multiline: true, placeholder: '{"page_id": "..."}' })}
              </div>
            </details>
          </div>
        )}
      </Dialog>
      <ConnectionReportDialog report={report} onClose={() => setReport(null)} />
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialAccountsView;
